import { hasValue, log, notEmpty } from "@repo/helpers";
import * as format from "@repo/helpers/src/format";

/** Regex for index, if property in array */
const INDEX_MATCH = /\[([0-9]+|'\w+')]/g;
/** Regex for finding sub-parameters (Test.SubParam) */
const SUB_PARAM_MATCH = /\[([0-9]?[A-Za-z][A-Za-z.[\]0-9]*)]/g;
const LOCALIZER_MATCH = /^this/g;
/** Regex that identifies properties embedded in templates */
const TEMPLATE_PROP_MATCH = /#= ?([:\w. [\]]+) ?#/g;

/**
 * Updates property on obj, by getting the correct property name, and updating the value
 * @param name Name of property to update
 * @param obj Object with property to update
 * @param value Value to set property to
 * @returns Updated Obj
 */
export function traversePropertyUpdate<TOut>(name: string, obj: TOut, value?: any) {
	if (!name) return null;
	name = getPropertyNameRecursive(name, obj);
	if (!name) return null;
	name = name.replace(/ /g, "");
	hydrateObjects(name, obj);
	return setPropertyValue(name, obj, value);
}

/**
 * Hydrates objects in the path of the property name
 * So if trying to set property on object that doesn't exist, this will bring it into existence
 * @param name Name of property to hydrate
 * @param parentObj Parent object to hydrate
 * @returns Parent object hydrated with properties
 */
function hydrateObjects<TObj>(name: string, parentObj: TObj) {
	let obj = parentObj;
	//Ignore the last key, as that will be the property we are setting
	const keys = name.split(".").filter(notEmpty).slice(0, -1);
	for (const key of keys) {
		obj[key] = obj[key] ?? ({} as TObj);
		const subKeys = key.split(INDEX_MATCH).filter(notEmpty);
		if (subKeys.length) obj[key] = obj[key] ?? [];
		for (const subKey of subKeys) {
			obj[subKey] = obj[subKey] ?? ({} as TObj);
			obj = obj[subKey] as TObj;
		}
	}
	return parentObj;
}

export const traversePropertyFn =
	<TOut>(obj: any, parentName?: string) =>
	(name: string) =>
		traverseProperty<TOut>(name, obj, parentName);

/**
 * Returns property value from obj
 * @param name full name of property
 * @param obj object property is on
 * @param parentName name of parent, for localizing
 */
export function traverseProperty<TOut>(name: string, obj: any, parentName?: string) {
	if (!name) return null;

	if (parentName) {
		name = localizeTraversedName(name, parentName);
	}
	name = getPropertyNameRecursive(name, obj);
	if (!name) return null;
	name = name.replace(/ /g, "");

	const value = getPropertyValue(name, obj) as TOut;
	if (!hasValue(value)) return null;
	return value;
}

/**
 * Gets property name of interpolated property (or just name, if not interpolated)
 * If, for example, prop name is settings.utilityInfo[lead.electricUtilityId],
 * and lead.electricUtilityId is 5, this will return settings.utilityInfo[5]
 * @param name Full name of property
 * @param obj Parent object that property is on
 * @param subProperty If true, return value (this would apply when getting the lead.electricUtilityId portion)
 * @returns Translated name
 */
export function getPropertyNameRecursive<TParent>(name: string, obj: TParent, subProperty = false) {
	if (!name) return undefined;
	name = name.replace(SUB_PARAM_MATCH, (match, subName = null) => getPropertyNameRecursive(subName, obj, true));
	if (subProperty) {
		const propValue = getPropertyValue(name, obj);
		name = hasValue(propValue) ? propValue : name;
		name = `[${name}]`;
	}
	return name;
}

/**
 * Sets value of property on object
 * @param name Name of property to set
 * @param obj Object to set property on
 * @param value New value of property
 * @returns Object with updated property
 */
function setPropertyValue<TParent>(name: string, obj: TParent, value: any) {
	let { currentObject, finalPropName } = getProperty(name, obj);

	if (!currentObject) currentObject = {} as TParent;
	currentObject[finalPropName] = value;
	return obj;
}

/**
 * Returns value of property on object
 * @param name Name of property
 * @param obj Object with property
 * @returns Value of property on an object
 */
function getPropertyValue<TParent>(name: string, obj: TParent) {
	const { currentObject, finalPropName } = getProperty(name, obj);
	return getPropertySubValue(finalPropName, currentObject);
}

/**
 * Walks through a chain of properties, and returns final object and property
 * @param name Name of prop (could be Locations, could be Locations[0], could be Locations[0].zipCode)
 * @param obj Object with property
 * @returns
 */
function getProperty<TParent>(name: string, obj: TParent) {
	let currentObject = obj as any;
	const keys = name.split(".");
	const finalPropName = keys.pop();
	for (const key of keys) {
		if (!key) continue;
		currentObject = getPropertySubValue(key, currentObject);
	}
	return { currentObject, finalPropName };
}

/**
 * If property has an index on it (like Locations[1]), handles that indexing and returns the second Location
 * @param name Name of prop
 * @param object Object that prop is on
 * @returns
 */
function getPropertySubValue<TObj>(name: string, object: TObj) {
	const subKeys = name.split(INDEX_MATCH);

	for (const subKey of subKeys) {
		if (!subKey) continue;
		if (!hasValue(object)) object = {} as TObj;
		object = object[subKey];
	}
	return object as any;
}

/** Replaces "this." in name with parent */
export function localizeTraversedName(name: string, parentName: string) {
	if (!name) return name;
	if (name === "this") return parentName;
	return name.replaceAll("this[", parentName + "[").replaceAll("this.", parentName + ".");
}

/**
 *
 * @param obj Parent Object with data to fill template with
 * @param rootProperty Section name, for localizing, aka filling "this"
 * @param template Template to replace with data values
 * @param index If property is iterated (i.e. "display this line for every item in this array"),
 * index is this index of this particular iteration
 * @param forDisplay
 * @returns Filled template
 */
export function fillTemplate<TProps>(
	obj: TProps,
	rootProperty: string,
	template: string,
	index?: number,
	forDisplay = true,
) {
	const { values } = breakdownTemplate(obj, rootProperty, template, index, forDisplay);
	return buildTemplateString(template, values);
}

export function breakdownTemplate<TProps>(
	obj: TProps,
	rootProperty: string,
	template: string,
	index?: number,
	forDisplay = true,
) {
	if (!template) return { values: [], properties: [] };
	const matches = template.matchAll(TEMPLATE_PROP_MATCH);
	const values = [];
	const properties = [];
	let currentMatch = matches.next();
	while (!currentMatch.done) {
		let matchValue = currentMatch.value[1] as string;
		if (matchValue === "index" && hasValue(index)) {
			properties.push(`${rootProperty}[${index}]`);
			values.push(index + 1);
		} else {
			let formatFn: (value: any) => string;
			if (matchValue.startsWith("format")) {
				const formatName = matchValue.split(":")[0].substring(7);
				formatFn = format[formatName];
				matchValue = matchValue.split(":")[1];
				if (!formatFn) log(`No matching format found for ${formatName} for Prop ${matchValue}`, "error");
			}
			const name = localizeTraversedName(matchValue, rootProperty);
			properties.push(getPropertyNameRecursive(name, obj));
			let value = traverseProperty(name, obj);
			if (forDisplay) {
				if (value === true) {
					value = "Yes";
				}
				if (value === false) {
					value = "No";
				}
			}
			if (formatFn) value = formatFn(value);
			values.push(value);
		}
		currentMatch = matches.next();
	}
	return { values, properties };
}

export function buildTemplateString(template: string, values: string | number[]) {
	const stringReplace = new RegExp(TEMPLATE_PROP_MATCH, "");
	let stringValue = "" + template;
	for (const value of values) {
		stringValue = stringValue.replace(stringReplace, () => String(value));
	}
	return stringValue;
}
